import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Tooltip con Portal: no se recorta por overflow y respeta z-index alto
// Uso: <Tooltip content="Texto" position="top"><span>i</span></Tooltip>
export default function Tooltip({
    content,
    children,
    position = 'top', // top | bottom | left | right
    delayMs = 150,
    disabled = false,
}) {
    const anchorRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, position: 'top' });
    const timerRef = useRef(null);

    const clear = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const show = () => {
        if (disabled || !content) return;
        clear();
        timerRef.current = setTimeout(() => setVisible(true), delayMs);
    };
    const hide = () => {
        clear();
        setVisible(false);
    };

    // Calcular posición respecto al viewport
    useEffect(() => {
        if (!visible || !anchorRef.current) return;

        const rect = anchorRef.current.getBoundingClientRect();
        const margin = 8; // separación del ancla

        let top = 0, left = 0;

        if (position === 'top') {
            top = rect.top - margin;
            left = rect.left + rect.width / 2;
        } else if (position === 'bottom') {
            top = rect.bottom + margin;
            left = rect.left + rect.width / 2;
        } else if (position === 'left') {
            top = rect.top + rect.height / 2;
            left = rect.left - margin;
        } else { // right
            top = rect.top + rect.height / 2;
            left = rect.right + margin;
        }

        setCoords({ top, left, position });
    }, [visible, position]);

    // Cerrar al hacer scroll o resize con debounce para evitar parpadeos
    useEffect(() => {
        if (!visible) return;

        let scrollTimeout;
        const close = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => setVisible(false), 50);
        };

        const immediateClose = () => setVisible(false);

        window.addEventListener('scroll', close, { passive: true });
        window.addEventListener('resize', immediateClose);

        return () => {
            clearTimeout(scrollTimeout);
            window.removeEventListener('scroll', close);
            window.removeEventListener('resize', immediateClose);
        };
    }, [visible]);

    const TooltipBubble = (
        <div
            role="tooltip"
            style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                zIndex: 9999,
                pointerEvents: 'none'
            }}
            className="will-change-auto"
        >
            <div
                className={`relative px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-xl whitespace-nowrap max-w-xs ${coords.position === 'top' ? '-translate-x-1/2 -translate-y-full' :
                    coords.position === 'bottom' ? '-translate-x-1/2' :
                        coords.position === 'left' ? '-translate-x-full -translate-y-1/2' :
                            '-translate-y-1/2'
                    }`}
            >
                {content}
                <span
                    className={`absolute w-2 h-2 bg-gray-900 rotate-45 ${coords.position === 'top' ? 'left-1/2 -translate-x-1/2 top-full' :
                        coords.position === 'bottom' ? 'left-1/2 -translate-x-1/2 -top-1' :
                            coords.position === 'left' ? 'top-1/2 -translate-y-1/2 left-full' :
                                'top-1/2 -translate-y-1/2 -left-1'
                        }`}
                />
            </div>
        </div>
    );

    return (
        <span
            ref={anchorRef}
            className="relative inline-flex"
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            {children}
            {visible && createPortal(TooltipBubble, document.body)}
        </span>
    );
}


